import React, { useEffect, useState } from 'react';
import { LOCALE_NAMES, LOCALE_OPTIONS, normalizeLocale } from '../../../../helpers/locales';
import {
    MARKET_CURRENCY_OPTIONS,
    normalizeMarketCountries,
} from '../../../../helpers/marketSettings';
import MarketCountryControls from '../../MarketCountryControls';
import HelpArticleLink from '../../HelpArticleLink';
import { Feedback, useSectionFeedback } from './sectionFeedback';

export default function InternationalizationSection({ data, saving, onSave, actions }) {
    const defaultLanguage = normalizeLocale(data?.defaultLanguage || 'en');
    const configuredLanguages = Array.isArray(data?.availableLanguages)
        ? data.availableLanguages.map(normalizeLocale).filter(Boolean)
        : [];
    const extraLanguages = [...new Set([defaultLanguage, ...configuredLanguages])]
        .filter(locale => !LOCALE_NAMES[locale]);
    const languageOptions = [
        ...LOCALE_OPTIONS,
        ...extraLanguages.map(locale => [locale, locale]),
    ];
    const [form, setForm] = useState({});
    const { feedback, showFeedback } = useSectionFeedback();

    useEffect(() => {
        setForm({
            defaultLanguage,
            availableLanguages: [...new Set([defaultLanguage, ...configuredLanguages])],
            defaultCurrency: data?.defaultCurrency || 'USD',
            shipToCountries: normalizeMarketCountries(
                data?.shipToCountries,
                [data?.countryCode || 'US'],
            ),
        });
    }, [data?.availableLanguages, data?.countryCode, data?.defaultCurrency,
        data?.shipToCountries, defaultLanguage]);

    function updateField(key, value) {
        setForm(current => ({ ...current, [key]: value }));
    }

    function updateDefaultLanguage(locale) {
        const nextDefaultLanguage = normalizeLocale(locale);
        setForm(current => ({
            ...current,
            defaultLanguage: nextDefaultLanguage,
            availableLanguages: [
                ...new Set([...(current.availableLanguages || []), nextDefaultLanguage]),
            ],
        }));
    }

    function toggleLanguage(locale, checked) {
        setForm(current => {
            const selected = new Set(current.availableLanguages || [current.defaultLanguage]);
            if (checked) selected.add(locale);
            else if (locale !== current.defaultLanguage) selected.delete(locale);
            return { ...current, availableLanguages: [...selected] };
        });
    }

    function handleSave() {
        onSave(async () => {
            const result = await actions.saveInternationalization(form);
            showFeedback(
                result.success ? 'success' : 'error',
                result.success ? 'Internationalization settings saved.' : result.error
            );
            return result;
        });
    }

    return (
        <div>
            <div className="sfm-section-header">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                    <h2>Internationalization</h2>
                    <HelpArticleLink
                        slug="storefront-translations"
                        label="Help with storefront translations"
                        variant="icon"
                    />
                </div>
                <p>Choose how visitors browse your storefront across languages and markets.</p>
            </div>
            <div className="group">
                <label htmlFor="sfm-default-language">Default Language</label>
                <select
                    id="sfm-default-language"
                    value={form.defaultLanguage || defaultLanguage}
                    disabled={saving}
                    onChange={event => updateDefaultLanguage(event.target.value)}
                    aria-describedby="sfm-default-language-help"
                >
                    {languageOptions.map(([locale, name]) => (
                        <option key={locale} value={locale}>{name} ({locale})</option>
                    ))}
                </select>
                <p id="sfm-default-language-help" className="help">
                    Used for the main storefront content and as the fallback when a translation is
                    unavailable.
                </p>
            </div>
            <fieldset className="group sfm-language-settings">
                <legend>Available Languages</legend>
                <p className="help">
                    Choose the languages visitors can select. The default language is always
                    available.
                </p>
                <div className="sfm-language-options">
                    {languageOptions.map(([locale, name]) => {
                        const isDefault = locale === form.defaultLanguage;
                        return (
                            <label
                                className="checkbox"
                                htmlFor={`sfm-language-${locale}`}
                                key={locale}
                            >
                                <input
                                    id={`sfm-language-${locale}`}
                                    type="checkbox"
                                    checked={(form.availableLanguages || []).includes(locale)}
                                    disabled={saving || isDefault}
                                    onChange={event => toggleLanguage(locale, event.target.checked)}
                                />
                                <span>
                                    {name} ({locale}){isDefault ? ' — Default' : ''}
                                </span>
                            </label>
                        );
                    })}
                </div>
            </fieldset>
            <div className="group">
                <label htmlFor="sfm-default-currency">Default Currency</label>
                <select
                    id="sfm-default-currency"
                    value={form.defaultCurrency || 'USD'}
                    disabled={saving}
                    onChange={event => updateField('defaultCurrency', event.target.value)}
                    aria-describedby="sfm-currency-help"
                >
                    {MARKET_CURRENCY_OPTIONS.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                    ))}
                </select>
                <p id="sfm-currency-help" className="help">
                    Used as the default currency for products in this storefront.
                </p>
            </div>
            <MarketCountryControls
                idPrefix="sfm"
                disabled={saving}
                shipToCountries={form.shipToCountries}
                onShipToCountriesChange={shipToCountries => (
                    updateField('shipToCountries', shipToCountries)
                )}
                shipToHelp={
                    'Select every country this storefront ships physical products to. '
                    + 'Product-level shipping settings can override this default.'
                }
            />
            <p className="help">
                Mayhem Marketplace is not yet available in every country, but we're working to
                expand.
            </p>
            <div className="sfm-save-row">
                <button
                    type="button"
                    className="button primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    Save Internationalization
                </button>
                <Feedback feedback={feedback} />
            </div>
        </div>
    );
}
